import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Divider, IconButton, Stack } from "@mzc-pdc/ui";
import { alpha } from "@mui/material";
import { IconAdd, IconCheckLarge, IconSchema } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import Typography from "@components_v2/typography/typography";
import PanelPropertyTitle from "@components/view/components/panel-property-title";
import MetadataFieldReadMode from "@features/metadata-fields/metadata-field-read-mode";
import { getMetadataIcon } from "@features/content-schema-fields/content-schema-type-icons";

/**
 * 커스텀 메타데이터 스키마 카드 (presentational, redux/fetch 없음).
 *
 * 스키마 헤더(아이콘 + 이름 + "필드 N")와 필드 목록(타입 아이콘 + 이름 + 필수 표시 + 하단 정보)을 렌더한다.
 * 프리셋 상세 패널 읽기 카드(definition)와 향후 AI Workspace 추출 결과 카드(result)가 공유한다.
 *
 * @param {object} props
 * @param {string} props.schemaName 스키마 이름
 * @param {Array<{id:string, subtitle:string, type:string, typeName?:string, isRequired:boolean, description?:string, value?:*, defaultValue?:*, options?:Array}>} props.fields
 *   `getCustomMetadataByContentSchema` 산출 필드 배열
 * @param {"definition"|"result"} [props.variant="definition"] 필드행 하단 표기 — definition: 필드 타입 이름, result: 값
 * @param {(field:object) => void} [props.onClickFieldAction] 필드행 우측 액션 버튼 클릭 핸들러. 전달 시에만 버튼을 노출한다(result 변형).
 * @param {string[]} [props.selectedFieldIds] 선택(적용)된 필드 id 목록. field.id 포함 시 해당 행/버튼을 selected로 표시.
 * @param {boolean} [props.hideFields=false] true면 헤더(이름 + "필드 N")만 렌더 — 편집 모드 선택 스키마 요약 카드용
 */
const CustomMetadataSchemaCard = ({
    schemaName,
    fields = [],
    variant = "definition",
    onClickFieldAction,
    selectedFieldIds,
    hideFields = false,
}) => {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                width: "100%",
                border: `1px solid ${theme.palette.greySecondary[100]}`,
                borderRadius: theme.spacing(0.5),
                overflow: "hidden",
            }}
        >
            <Stack
                direction={"row"}
                alignItems={"center"}
                gap={1}
                sx={{ px: 1.5, py: 1, backgroundColor: alpha(theme.palette.greySecondary[50], 0.24) }}
            >
                <IconSchema size={16} htmlColor={theme.palette.success.main} />
                <Typography
                    variant={"body1_500"}
                    color={theme.palette.greySecondary.main}
                    sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                    {schemaName}
                </Typography>
                <Typography
                    variant={"body3_400"}
                    color={theme.palette.greySecondary[600]}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    {t(`common::label::Field {{count}}`, `Field {{count}}`, { count: fields.length })}
                </Typography>
            </Stack>

            {!hideFields &&
                fields.map((field, index) => {
                    const isSelected = !!selectedFieldIds?.includes(field.id);
                    return (
                        <Box key={field.id ?? index}>
                            <Divider sx={{ borderColor: theme.palette.greySecondary[100] }} />
                            <Stack
                                direction={"row"}
                                alignItems={"flex-start"}
                                gap={1.5}
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    ...(isSelected && {
                                        backgroundColor: alpha(theme.palette.greySecondary.A100, 0.4),
                                    }),
                                }}
                            >
                                <Box
                                    width={20}
                                    height={20}
                                    sx={{
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: theme.spacing(0.25),
                                        backgroundColor: theme.palette.secondary[800],
                                    }}
                                >
                                    {getMetadataIcon(field.type)}
                                </Box>
                                <Stack direction={"column"} flex={1} sx={{ minWidth: 0 }}>
                                    <Stack direction={"row"} flex={1}>
                                        <PanelPropertyTitle
                                            variant={"body3"}
                                            color={theme.palette.greySecondary[600]}
                                            required={field.isRequired}
                                            subtitle={field.subtitle}
                                            tooltip={field.description}
                                        />
                                    </Stack>
                                    <Typography variant={"body1"} color={theme.palette.greySecondary.main}>
                                        {variant === "result" ? (
                                            <MetadataFieldReadMode
                                                type={field.type}
                                                isHidden={false}
                                                value={field.value ?? field.defaultValue}
                                                options={field.options}
                                            />
                                        ) : (
                                            field.typeName
                                        )}
                                    </Typography>
                                </Stack>
                                {onClickFieldAction && (
                                    <IconButton
                                        circled
                                        size={"small"}
                                        onClick={() => onClickFieldAction(field)}
                                        sx={{
                                            flexShrink: 0,
                                            alignSelf: "center",
                                            ...(isSelected
                                                ? { backgroundColor: theme.palette.primary.main }
                                                : { border: `1px solid ${theme.palette.primary.main}` }),
                                        }}
                                    >
                                        {isSelected ? (
                                            <IconCheckLarge size={12} htmlColor={theme.palette.common.white} />
                                        ) : (
                                            <IconAdd size={12} htmlColor={theme.palette.primary.main} />
                                        )}
                                    </IconButton>
                                )}
                            </Stack>
                        </Box>
                    );
                })}
        </Box>
    );
};

CustomMetadataSchemaCard.propTypes = {
    schemaName: PropTypes.string,
    fields: PropTypes.array,
    variant: PropTypes.oneOf(["definition", "result"]),
    onClickFieldAction: PropTypes.func,
    selectedFieldIds: PropTypes.arrayOf(PropTypes.string),
    hideFields: PropTypes.bool,
};

export default CustomMetadataSchemaCard;
